/**
 * GA4-02, then GA4-14 — the consent record, and the only place that decides
 * what the visitor has agreed to.
 *
 * WHY THIS EXISTS. GA4-01 and GA4-13 answered "may this hit be sent from this
 * route?". Neither of them asked the visitor anything: the tag booted on every
 * allowed route, for everyone, on the first paint. This module is the second
 * factor. Nothing about GA4 — not the loader, not the inline bootstrap, not a
 * single `gtag()` call — may happen until `isAnalyticsConsentGranted()` is true.
 *
 * BASIC CONSENT MODE, and what that rules out. The locked decision for GA4-02
 * is that gtag.js is NOT LOADED AT ALL before consent. Advanced Consent Mode
 * (load the tag, deny the signals, let Google collect cookieless pings) was
 * refused: a cookieless ping is still a request to Google carrying the
 * visitor's IP and page URL, and the objective is that nothing leaves the
 * browser. So consent is a load-time gate, not a parameter on a hit.
 *
 * ─── WHAT CHANGED IN v2 (GA4-14) ─────────────────────────────────────────────
 * The product gained a second purpose. Advertising — Google Ads and Meta
 * remarketing — is not analytics, is not covered by the analytics ask, and can
 * never be inferred from it. So the record carries TWO independent decisions
 * rather than one:
 *
 *     {
 *       "version": 2,
 *       "analytics":   { "status": "granted" | "denied", "timestamp": "<ISO>" },
 *       "advertising": { "status": "granted" | "denied", "timestamp": "<ISO>" }
 *     }
 *
 * Each category carries its OWN timestamp. That is not decoration: several EU
 * DPAs suggest a shorter life for advertising consent than for analytics, and a
 * single document-level timestamp would make that unimplementable without
 * another schema bump. Today both age out at twelve months; the shape is what
 * makes shortening one of them a one-constant change (recon R10).
 *
 * THE v1 → v2 RE-PROMPT IS DELIBERATE (PO decision D3, 2026-08-31). Every v1
 * record is discarded, including every v1 `denied` — `parseConsent` does not
 * branch on status when the version is wrong. That re-asks people who already
 * said no, which reads as consent fatigue and was accepted anyway: v1 consent
 * was given against copy that described analytics and nothing else, so reading
 * it as an answer about advertising would be inventing a permission. A version
 * bump is the ONLY legitimate re-prompt in this system. Logging out is not one
 * — see the carve-out in contexts/AuthContext.tsx.
 *
 * GLOBAL PRIVACY CONTROL (PO decision D2). `navigator.globalPrivacyControl`
 * is a legally recognised opt-out signal under CCPA/CPRA. While it is on,
 * advertising consent CANNOT be granted: it is coerced to 'denied' on the way
 * in (`setConsent`, `updateConsent`) and again on the way out
 * (`getConsentSnapshot`). Both, not either — writing alone would let a grant
 * made before the visitor turned GPC on go on counting, and reading alone would
 * store a permission the law says we may not hold. The stored record is left
 * intact rather than rewritten, so turning GPC back off restores the answer the
 * visitor actually gave instead of silently converting it to a refusal.
 *
 * WHY IT DOES NOT IMPORT utils/gaGuard.ts. gaGuard imports THIS module — the
 * bootstrap gate needs `isAnalyticsConsentGranted()` — so an import back the
 * other way would be a cycle, and a cycle between two modules that both run at
 * import time is how a bundler hands you a TDZ error in production and nowhere
 * else. Instead this module publishes a change and gaGuard subscribes:
 * `setConsent` persists and notifies, and gaGuard's listener is what performs
 * "bootstrap, consent update, one page view".
 */

/** A decision the visitor has actually made. */
export type ConsentDecision = 'granted' | 'denied';

/** …plus the state where they have not been asked, or their answer expired. */
export type ConsentStatus = ConsentDecision | 'unset';

/** The two things the visitor is asked about, independently. */
export type ConsentCategory = 'analytics' | 'advertising';

/** Both categories, in the order the banner and the settings panel show them. */
export const CONSENT_CATEGORIES: readonly ConsentCategory[] = ['analytics', 'advertising'];

/** The localStorage key. Named for the product so it cannot collide. */
export const CONSENT_STORAGE_KEY = 'afriqahome_consent';

/**
 * Is this the consent record?
 *
 * Exported for ONE caller: the logout sweep in contexts/AuthContext.tsx, which
 * removes every `afriqahome_`-prefixed key and must not remove this one (PO
 * decision 2026-08-31, Option A — consent is device-scoped, not account-scoped).
 * It lives here rather than there so the sweep cannot drift from the key it is
 * meant to spare, and so the carve-out is testable without importing a React
 * context.
 */
export function isConsentStorageKey(key: string): boolean {
  return key === CONSENT_STORAGE_KEY;
}

/**
 * The schema version of the stored record.
 *
 * Bumping this re-prompts everybody, which is the point: it is the lever for
 * "we now collect something the previous banner did not describe". A record
 * written by ANY other version — older or newer — is treated as no record at
 * all, so a rollback cannot silently honour a choice made against copy this
 * build never showed.
 */
export const CONSENT_VERSION = 2;

/** Twelve months, after which a stored choice stops counting as current. */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** One category's answer, as stored. */
export interface StoredCategoryConsent {
  status: ConsentDecision;
  timestamp: string;
}

export interface StoredConsent {
  version: number;
  analytics: StoredCategoryConsent;
  advertising: StoredCategoryConsent;
}

/** A decision covering both categories, as the banner's buttons make it. */
export interface ConsentChoices {
  analytics: ConsentDecision;
  advertising: ConsentDecision;
}

/**
 * What the visitor's storage means right now.
 *
 * `superseded` is not a third opinion about consent — it is the reason the two
 * statuses are 'unset'. True only when an intact record WAS found and was
 * discarded because it carried a different schema version. The banner uses it
 * to say "we've updated how we use cookies" instead of greeting a returning
 * visitor as if they had never been asked. Malformed JSON and an absent record
 * are both plain first visits and leave it false.
 */
export interface ConsentSnapshot {
  analytics: ConsentStatus;
  advertising: ConsentStatus;
  superseded: boolean;
}

const UNSET_SNAPSHOT: ConsentSnapshot = Object.freeze({
  analytics: 'unset',
  advertising: 'unset',
  superseded: false,
});

/**
 * Is the browser telling us not to sell or share?
 *
 * Feature-detected on every call rather than captured once: an extension can
 * set it after load, and a stale `false` would be a permission we are not
 * entitled to. Guarded for the server, where there is no navigator at all.
 */
export function isGlobalPrivacyControlEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
}

/** GPC downgrades a GRANT and nothing else — it never manufactures a decision. */
function applyGpc(advertising: ConsentStatus): ConsentStatus {
  if (advertising === 'granted' && isGlobalPrivacyControlEnabled()) return 'denied';
  return advertising;
}

/** The exact bytes written to localStorage for a decision in both categories. */
export function serializeConsent(choices: ConsentChoices, at: Date | number = Date.now()): string {
  const timestamp = new Date(at).toISOString();
  return serializeRecord({
    version: CONSENT_VERSION,
    analytics: { status: choices.analytics, timestamp },
    advertising: { status: choices.advertising, timestamp },
  });
}

/** The same, for a record whose two categories carry different timestamps. */
function serializeRecord(record: StoredConsent): string {
  return JSON.stringify(record);
}

/** One category of a parsed record: intact, in-date, and a real decision — or 'unset'. */
function parseCategory(value: unknown, now: number): ConsentStatus {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'unset';

  const entry = value as Partial<StoredCategoryConsent>;
  if (entry.status !== 'granted' && entry.status !== 'denied') return 'unset';
  if (typeof entry.timestamp !== 'string') return 'unset';

  const writtenAt = Date.parse(entry.timestamp);
  if (Number.isNaN(writtenAt)) return 'unset';
  if (now - writtenAt > CONSENT_MAX_AGE_MS) return 'unset';

  return entry.status;
}

/**
 * The re-prompt rule, as a pure function: what a stored string means at `now`.
 *
 * Anything that is not an intact, current, in-date record of a real decision is
 * `'unset'` — which the rest of the app treats exactly like `'denied'` for the
 * purpose of sending anything, and unlike it for the purpose of showing the
 * banner.
 *
 * EXPIRY IS PER CATEGORY, evaluated against that category's own timestamp. One
 * can age out while the other stays valid, and the banner then asks about the
 * one that lapsed. Version and structural failures are per RECORD: a record
 * this build cannot read is no record at all, in both categories.
 *
 * A timestamp in the FUTURE is honoured rather than discarded. It means a
 * skewed clock, and re-prompting somebody because their laptop is set to 2027
 * would be a worse outcome than trusting the answer they gave.
 *
 * NOT GPC-AWARE, on purpose. This is the pure reader of what is on disk;
 * `getConsentSnapshot` is where a live browser signal is layered on top. Keeping
 * the two apart is what lets the version, expiry and structure branches be
 * tested without a navigator.
 */
export function parseConsent(raw: string | null | undefined, now: number = Date.now()): ConsentSnapshot {
  if (!raw) return UNSET_SNAPSHOT;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return UNSET_SNAPSHOT;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return UNSET_SNAPSHOT;

  const record = parsed as Partial<StoredConsent>;
  if (record.version !== CONSENT_VERSION) {
    // A record we cannot read, but a record. Only a WRITTEN version counts as
    // superseded — a blob with no version at all is corruption, not a visitor
    // whose earlier answer this build has retired, and telling them we changed
    // our cookie use would be a claim about their history we cannot support.
    return { analytics: 'unset', advertising: 'unset', superseded: typeof record.version === 'number' };
  }

  return {
    analytics: parseCategory(record.analytics, now),
    advertising: parseCategory(record.advertising, now),
    superseded: false,
  };
}

/**
 * Storage that throws is storage with no consent in it.
 *
 * Safari with site data blocked, and any browser in a locked-down profile,
 * throws on `localStorage` access rather than returning null. The house rule in
 * src/components/home/useClientOnlyStorage.ts is "a prompt one visitor sees
 * twice is recoverable"; here the same rule points the other way and is
 * stronger, because the recoverable outcome is also the private one — no
 * readable record means no consent means no tag.
 */
function readStoredConsent(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The stored record itself, when it is one this build can read.
 *
 * `updateConsent` needs the raw record rather than the parsed statuses, because
 * preserving the untouched category means preserving its TIMESTAMP — and a
 * status alone cannot say when it was given.
 */
function readStoredRecord(): StoredConsent | null {
  const raw = readStoredConsent();
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const record = parsed as Partial<StoredConsent>;
  if (record.version !== CONSENT_VERSION) return null;

  return record as StoredConsent;
}

/**
 * The visitor's current answer, in both categories.
 *
 * Deliberately NOT cached in a module variable. It is read from storage on
 * every call — on every navigation, before every hit — so a choice made in
 * another tab is honoured here at the next thing this tab does, and so a stored
 * record that crosses the twelve-month line mid-session stops counting without
 * anything having to invalidate it.
 *
 * Storage wins per category whenever it has an answer; the fallback fills in
 * only for the browser that could not keep one — see `sessionFallback`. GPC is
 * applied last, over both, because it outranks anything we have stored.
 */
export function getConsentSnapshot(now: number = Date.now()): ConsentSnapshot {
  const stored = parseConsent(readStoredConsent(), now);
  const fallback = sessionFallback;

  const analytics = stored.analytics !== 'unset' ? stored.analytics : fallback?.analytics ?? 'unset';
  const advertising =
    stored.advertising !== 'unset' ? stored.advertising : fallback?.advertising ?? 'unset';

  return {
    analytics,
    advertising: applyGpc(advertising),
    superseded: stored.superseded,
  };
}

/** One category's current answer. */
export function getConsentStatus(category: ConsentCategory, now: number = Date.now()): ConsentStatus {
  return getConsentSnapshot(now)[category];
}

/** The predicate the two bootstrap paths and the event guard are gated on. */
export function isAnalyticsConsentGranted(): boolean {
  return getConsentStatus('analytics') === 'granted';
}

/**
 * Advertising consent. NOTHING consumes this yet.
 *
 * The tag layer is GA4-14's third spoke; until it lands, the only thing a grant
 * here changes is which Consent Mode signals `buildConsentPayload` declares
 * granted. That is deliberate — collecting the permission and acting on it are
 * separate releases, and this is the one that collects it.
 */
export function isAdvertisingConsentGranted(): boolean {
  return getConsentStatus('advertising') === 'granted';
}

/**
 * The analytics predicate, under its original name.
 *
 * Kept because "consent" meant analytics for the whole of GA4-02 and callers
 * outside this item still read it that way — src/pages/+Head.tsx among them.
 * It is an alias, not a summary of both categories: there is no state in which
 * advertising consent alone should boot gtag.js.
 */
export function isConsentGranted(): boolean {
  return isAnalyticsConsentGranted();
}

type ConsentListener = (snapshot: ConsentSnapshot) => void;

const listeners = new Set<ConsentListener>();

let storageSyncInstalled = false;

/**
 * Cross-tab revocation.
 *
 * `storage` fires in the OTHER tabs of the origin, which is the only signal a
 * tab already running a booted tag gets when the visitor withdraws consent
 * somewhere else. gaGuard's listener answers it by holding GA4's opt-out flag
 * down, so the withdrawal takes effect in those tabs immediately rather than at
 * their next navigation. A `key` of null is `localStorage.clear()`.
 */
function installStorageSync(): void {
  if (storageSyncInstalled || typeof window === 'undefined') return;
  storageSyncInstalled = true;
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && event.key !== CONSENT_STORAGE_KEY) return;

    // PR #701 review, P1 — DROP THE FALLBACK FIRST.
    //
    // Another tab has just spoken about this key, and its word is a fact about
    // storage; `sessionFallback` is only this document's memory of something it
    // could not write down. Without this line a cross-tab change read back as
    // 'unset' and was then replaced by the stale fallback, so a document
    // holding a failed 'granted' kept GA4 running and kept the banner shut
    // after the visitor had withdrawn consent next door.
    //
    // The failure that makes this reachable is a full quota, not a locked-down
    // browser: `setItem` throws on QuotaExceededError while `getItem` keeps
    // working and another tab's `removeItem` succeeds — removing frees space.
    // Which is also why the fix is HERE and not in `getConsentSnapshot`: in that
    // browser a readable `null` cannot be treated as authoritative, or Accept
    // would go inert again for exactly the case the fallback exists to serve.
    //
    // Unconditional, not just on a removal (`event.newValue === null`). A
    // cross-tab WRITE supersedes the fallback too — storage then has a real
    // record, which already wins — so one rule covers both and there is no
    // second branch to get wrong.
    sessionFallback = null;

    notify(getConsentSnapshot());
  });
}

/**
 * How often the twelve-month line is checked on a document that stays open.
 *
 * A minute. The check is one `localStorage` read and one `JSON.parse` of a
 * ~160-byte string, so the cost is not worth optimising, and the exposure it
 * bounds — a tag still sending after its consent expired — is the exact thing
 * this module exists to prevent.
 */
export const CONSENT_RECHECK_INTERVAL_MS = 60_000;

/** The snapshot the listeners were last told about. */
let lastAnnounced: ConsentSnapshot | null = null;

let expiryWatchInstalled = false;

/** Only the two decisions count as a change; `superseded` is a reason, not a state. */
function sameDecisions(a: ConsentSnapshot, b: ConsentSnapshot): boolean {
  return a.analytics === b.analytics && a.advertising === b.advertising;
}

/**
 * Re-read the record and announce a change if there is one.
 *
 * Exported because a caller may need the answer NOW rather than at the next
 * tick — the browser suite drives it directly rather than waiting a minute, and
 * an app that knows it has been idle can call it on resume.
 *
 * Notifying only on a CHANGE is what makes it safe to call as often as anyone
 * likes: it is the diff, not the poll, that reaches the listeners.
 */
export function recheckConsentNow(): ConsentSnapshot {
  const current = getConsentSnapshot();
  if (lastAnnounced !== null && sameDecisions(current, lastAnnounced)) return current;
  notify(current);
  return current;
}

/**
 * Watch for a grant ageing out from under a document that is still open.
 *
 * WHY A TIMER AT ALL. `getConsentSnapshot` is honest the instant a record turns
 * twelve months old, and every gate that reads it — `ensureGtagBootstrapped`,
 * `canSendHit` — goes cold with it. What does NOT go cold is gtag.js: once
 * loaded, its Enhanced Measurement listeners are attached to the document, they
 * never consult this module, and the only thing that silences them is GA4's
 * opt-out flag, which utils/gaGuard.ts re-derives on navigation. A document
 * sitting open across the boundary would therefore keep sending scrolls and
 * clicks until the visitor happened to navigate. This closes that window, and
 * the same notification reopens the banner so they can answer again.
 *
 * `visibilitychange` as well as the interval, because a backgrounded tab has
 * its timers throttled hard — returning to the tab is the moment that matters,
 * and it is also when a hidden tab's clock may have jumped.
 */
function installExpiryWatch(): void {
  if (expiryWatchInstalled || typeof window === 'undefined') return;
  expiryWatchInstalled = true;

  if (lastAnnounced === null) lastAnnounced = getConsentSnapshot();

  // Feature-detected, not assumed. This runs at IMPORT time — utils/gaGuard.ts
  // subscribes at module scope — so a host with a partial `window` shim would
  // otherwise take the whole bundle down on load rather than merely lose the
  // watch. `recheckConsentNow` stays callable either way.
  if (typeof window.setInterval === 'function') {
    window.setInterval(recheckConsentNow, CONSENT_RECHECK_INTERVAL_MS);
  }

  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) recheckConsentNow();
    });
  }
}

function notify(snapshot: ConsentSnapshot): void {
  lastAnnounced = snapshot;
  for (const listener of listeners) listener(snapshot);
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 *
 * Two subscribers exist: utils/gaGuard.ts, which performs the GA4 side of a
 * change, and the banner, which re-opens when a category goes back to 'unset'.
 */
export function onConsentChange(listener: ConsentListener): () => void {
  installStorageSync();
  installExpiryWatch();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The decision this document is running under when it could not be written down.
 *
 * PER CATEGORY since v2. It was a single scalar in v1, and leaving it that way
 * while the record grew two categories would have silently regressed the exact
 * bug PR #700's review added it for: in a browser with site data blocked, a
 * choice that cannot be persisted is a choice that does nothing, because every
 * listener answers by READING STORAGE BACK. One category's answer must not
 * stand in for the other's (recon R5).
 *
 * Null in the normal case, where storage IS the answer and this must never
 * shadow it. It is set only when a write actually failed, which in practice
 * means a browser with site data blocked — and in that browser every read
 * returns nothing, so there is no stored record for it to contradict.
 *
 * It lives for the pageview and no longer, and it is dropped the moment another
 * tab says anything about the key — see `installStorageSync`.
 *
 * MODULE-GLOBAL, AND MUST NEVER BE WRITTEN DURING SSR (recon R8). This module
 * is imported by src/pages/+Head.tsx, and on the server the module instance is
 * long-lived ACROSS REQUESTS — so a `setConsent`, `updateConsent` or
 * `clearConsent` reached during a server render would leak one visitor's
 * decision into every subsequent render's `getConsentSnapshot()`. All three
 * writers return early without a `window`, which is what keeps that impossible;
 * do not add a server-side migration path that bypasses them.
 */
let sessionFallback: { analytics: ConsentStatus; advertising: ConsentStatus } | null = null;

/** True when the value reached storage. */
function writeStoredConsent(value: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (value === null) window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    else window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Record a decision in BOTH categories, and act on it.
 *
 * The banner's "Accept all" and "Reject all" both land here: they are answers
 * to the whole question, so they write both categories with the SAME timestamp.
 * A change to one category alone is `updateConsent`.
 *
 * Persist first, notify second, because gaGuard's listener boots the tag by
 * calling back into `isAnalyticsConsentGranted()` — it must see the record this
 * call just wrote, not the one it is replacing.
 */
export function setConsent(choices: ConsentChoices, at: Date | number = Date.now()): void {
  const coerced: ConsentChoices = {
    analytics: choices.analytics,
    // D2: advertising can never be stored as granted while GPC is on.
    advertising: applyGpc(choices.advertising) === 'granted' ? 'granted' : 'denied',
  };

  const persisted = writeStoredConsent(serializeConsent(coerced, at));
  // Persisted: storage is the record and the fallback must not shadow it.
  // Not persisted: this is the only place the decision now exists.
  sessionFallback = persisted ? null : { ...coerced };
  notify(getConsentSnapshot());
}

/**
 * Change ONE category and leave the other exactly as it was — the settings
 * panel's "Save choices", and the reason GA4-14 could retire `clearConsent`
 * from the UI.
 *
 * GDPR Art. 7(3) wants withdrawal to be as easy as consent. Under v1 the only
 * way to change your mind was `clearConsent()`, which wiped the record and
 * re-asked everything; with two categories that means a visitor who wants to
 * turn advertising off has to re-answer analytics too, and analytics goes cold
 * in between (recon R6). This writes the one category that moved.
 *
 * PRESERVES THE UNTOUCHED CATEGORY'S TIMESTAMP, not just its status. The
 * timestamp is what its expiry is measured from, so copying the status while
 * stamping "now" would silently extend a consent the visitor did not renew.
 *
 * With no readable record — a first visit that starts in the panel rather than
 * on the banner — the untouched category is written as 'denied' at the same
 * instant. Both toggles start off, so leaving one off and pressing Save IS a
 * refusal, and the schema has no way to say "still unanswered".
 */
export function updateConsent(
  partial: Partial<Record<ConsentCategory, ConsentDecision>>,
  at: Date | number = Date.now(),
): void {
  const timestamp = new Date(at).toISOString();
  const existing = readStoredRecord();

  const resolve = (category: ConsentCategory): StoredCategoryConsent => {
    const next = partial[category];
    if (next) return { status: next, timestamp };

    const kept = existing?.[category];
    if (kept && (kept.status === 'granted' || kept.status === 'denied') && typeof kept.timestamp === 'string') {
      return { status: kept.status, timestamp: kept.timestamp };
    }

    // No readable record — but this document may still be holding an answer it
    // could not write down. Without this branch, saving one category in a
    // site-data-blocked browser would silently overwrite the other with a
    // refusal the visitor never gave. There is no stored timestamp to preserve
    // in that case, so the fresh one is the honest stamp.
    const held = sessionFallback?.[category];
    if (held === 'granted' || held === 'denied') return { status: held, timestamp };

    return { status: 'denied', timestamp };
  };

  const record: StoredConsent = {
    version: CONSENT_VERSION,
    analytics: resolve('analytics'),
    advertising: resolve('advertising'),
  };

  // D2 again, on the path the panel actually uses. The toggle is rendered
  // locked when GPC is on, so this is defence in depth rather than the only
  // guard — but a caller that never renders is exactly what a guard is for.
  if (record.advertising.status === 'granted' && isGlobalPrivacyControlEnabled()) {
    record.advertising = { status: 'denied', timestamp };
  }

  const persisted = writeStoredConsent(serializeRecord(record));
  sessionFallback = persisted
    ? null
    : { analytics: record.analytics.status, advertising: record.advertising.status };
  notify(getConsentSnapshot());
}

/**
 * Forget the decision and go back to asking.
 *
 * NO UI CALLS THIS as of GA4-14 — the footer's "Cookie settings" link opens the
 * settings panel instead (PO decision D4), because clearing is not the same as
 * changing your mind and a visitor who wants advertising off should not have to
 * re-answer analytics to get there. Kept exported because it is still the
 * honest way to express "this record should not exist", which the test suites
 * need in order to set up an unasked visitor.
 *
 * Clearing is not the same as declining. It reopens the banner, and until the
 * visitor answers, both categories are 'unset' — so nothing new may boot, and
 * anything already booted is suppressed by the same listener that handles a
 * decline.
 */
export function clearConsent(): void {
  const persisted = writeStoredConsent(null);
  sessionFallback = persisted ? null : { analytics: 'unset', advertising: 'unset' };
  notify(getConsentSnapshot());
}
